import React, { useState } from 'react';
import { Dish, OrderItem } from '../types';
import { useOrder } from '../context/OrderContext';

interface DishCardProps {
  dish: Dish;
  restaurantName: string;
  restaurantId: string;
  globalDiscount?: number;
  onPress?: (dish: Dish) => void;
  isOffline?: boolean;
  editModeParams?: {
    items: OrderItem[];
    onUpdateItems: (items: OrderItem[]) => void;
  };
}

const DishCard: React.FC<DishCardProps> = ({ dish, restaurantName, restaurantId, globalDiscount = 0, onPress, isOffline = false, editModeParams }) => {
  const { addToOrder, updateQuantity, cartOrders } = useOrder();

  const effectiveDiscount = dish.discount && dish.discount > 0 ? dish.discount : globalDiscount;
  const hasDiscount = effectiveDiscount > 0;
  const finalPrice = hasDiscount ? Math.round(dish.price * (1 - effectiveDiscount / 100)) : dish.price;

  const cartItem = editModeParams 
    ? editModeParams.items.find(item => String(item.dish.id) === String(dish.id))
    : cartOrders.flatMap(o => o.items).find(item => item.dish.id === dish.id);
  const quantity = cartItem ? cartItem.quantity : 0;

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (quantity === 0) {
      if (editModeParams) {
        const newItem: OrderItem = { dish: { ...dish, price: finalPrice }, quantity: 1, restaurantName, restaurantId, selectedExtras: [] };
        editModeParams.onUpdateItems([...editModeParams.items, newItem]);
      } else {
        addToOrder({ ...dish, price: finalPrice }, restaurantName, restaurantId);
      }
    } else {
      if (editModeParams) {
        const newItems = editModeParams.items.map(item => String(item.dish.id) === String(dish.id) ? { ...item, quantity: quantity + 1 } : item);
        editModeParams.onUpdateItems(newItems);
      } else {
        updateQuantity(dish.id, quantity + 1, restaurantId);
      }
    }
  };

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (quantity > 0) {
      if (editModeParams) {
        if (quantity === 1) {
          editModeParams.onUpdateItems(editModeParams.items.filter(item => String(item.dish.id) !== String(dish.id)));
        } else {
          const newItems = editModeParams.items.map(item => String(item.dish.id) === String(dish.id) ? { ...item, quantity: quantity - 1 } : item);
          editModeParams.onUpdateItems(newItems);
        }
      } else {
        updateQuantity(dish.id, quantity - 1, restaurantId);
      }
    }
  };

  return (
    <div
      className={`bg-white rounded-[2rem] shadow-xl overflow-hidden flex flex-col relative flex-shrink-0 w-[210px] h-[300px] cursor-pointer group active:scale-[0.98] transition-all duration-300 isolation-isolate z-0 ${isOffline ? 'grayscale opacity-80' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onPress && onPress(dish);
      }}
    >
      {/* Upper Content Section (Image & Badge) */}
      <div className="relative h-56 w-full overflow-hidden">
        <img
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          src={dish.image}
          alt={dish.name}
        />
        
        {/* Gradient overlay removed or kept subtle for badge */}
        <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-black/20 to-transparent pointer-events-none" />

        {/* [NEW] Closed Badge for Items */}
        {isOffline && (
            <div className="absolute inset-0 flex items-center justify-center z-30">
                <span className="bg-black/60 backdrop-blur-sm text-white text-[8px] font-black uppercase px-3 py-1 rounded-full border border-white/20">Fermé</span>
            </div>
        )}

        {/* Discount Badge */}
        {hasDiscount && (
          <div className="absolute top-4 right-4 bg-[#E31E24] text-white text-[9px] font-black px-2 py-1 rounded-lg z-20 shadow-xl border-b-2 border-black/20">
            - {effectiveDiscount}%
          </div>
        )}
      </div>

      {/* Bottom Bar Section */}
      <div className="bg-white px-5 py-4 flex flex-col flex-1 justify-start">
        <div className="text-left">
          <h4 className="font-black text-sm leading-tight text-gray-900 uppercase italic tracking-tighter line-clamp-1">
            {dish.name}
          </h4>
          <p className="text-[9px] font-bold text-gray-400 line-clamp-2 uppercase tracking-wide leading-tight">
            {dish.description}
          </p>
        </div>

        <div className="flex items-center justify-between mt-1">
          <div className="flex flex-col">
            {hasDiscount && (
              <span className="text-[9px] text-gray-400 line-through leading-none mb-0.5 font-bold">{dish.price}</span>
            )}
            <span className="text-[15px] font-black text-gray-900 leading-none italic tracking-tighter">
              {finalPrice} <span className="text-[9px] font-bold text-[#E31E24] uppercase tracking-widest not-italic ml-0.5">DA</span>
            </span>
          </div>

          {!isOffline && (
            quantity === 0 ? (
              <button
                onClick={handleIncrement}
                className="w-8 h-8 rounded-full bg-[#E31E24] text-white flex items-center justify-center shadow-lg shadow-red-100 hover:bg-red-700 active:scale-90 transition-all border-b-[3px] border-black/10 translate-x-[2px]"
              >
                <span className="text-xl font-black">+</span>
              </button>
            ) : (
              <div className="flex items-center bg-gray-50 rounded-full p-0.5 border border-gray-100 shadow-inner translate-x-[2px]">
                <button
                  onClick={handleDecrement}
                  className="w-6 h-6 rounded-full bg-white text-[#E31E24] flex items-center justify-center shadow-sm text-[10px] font-black active:scale-90 transition-all"
                >
                  -
                </button>
                <span className="mx-2 text-[10px] font-black text-gray-900 min-w-[10px] text-center">{quantity}</span>
                <button
                  onClick={handleIncrement}
                  className="w-6 h-6 rounded-full bg-[#E31E24] text-white flex items-center justify-center shadow-sm text-[10px] font-black active:scale-90 transition-all"
                >
                  +
                </button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default DishCard;